// Parse a voice-transcribed food query. Extract the food name and (optionally)
// a quantity + unit. Supports IT/EN/DE/FR word-numbers and short unit forms.
//
// Examples:
//   "kiwi 100g"            → { query: "kiwi",   quantity_g: 100 }
//   "due kiwi"             → { query: "kiwi",   pieces: 2 }
//   "150 grammi di pollo"  → { query: "pollo",  quantity_g: 150 }
//   "half apple"           → { query: "apple",  pieces: 0.5 }
//   "eiweiss 200 gramm"    → { query: "eiweiss", quantity_g: 200 }
//   "kiwi"                 → { query: "kiwi" }

const WORD_NUMS = {
  // Italian
  "uno": 1, "un": 1, "una": 1, "due": 2, "tre": 3, "quattro": 4, "cinque": 5,
  "sei": 6, "sette": 7, "otto": 8, "nove": 9, "dieci": 10,
  "mezzo": 0.5, "mezza": 0.5,
  // English
  "a": 1, "an": 1, "one": 1, "two": 2, "three": 3, "four": 4, "five": 5,
  "six": 6, "seven": 7, "eight": 8, "nine": 9, "ten": 10, "half": 0.5,
  // German
  "ein": 1, "eine": 1, "einen": 1, "zwei": 2, "drei": 3, "vier": 4, "fünf": 5,
  "sechs": 6, "sieben": 7, "acht": 8, "neun": 9, "zehn": 10, "halb": 0.5,
  "halbe": 0.5, "halber": 0.5,
  // French
  "un": 1, "une": 1, "deux": 2, "trois": 3, "quatre": 4, "cinq": 5,
  "six_fr": 6, "sept": 7, "huit": 8, "neuf": 9, "dix": 10, "demi": 0.5, "demie": 0.5,
};

// Unit tokens → normalized grams multiplier (or "piece")
const UNITS = {
  g: 1, gr: 1, gs: 1, grams: 1, gramm: 1, grammi: 1, gramme: 1, grammes: 1, grammo: 1,
  kg: 1000, kilogram: 1000, kilograms: 1000, kilogramm: 1000, chili: 1000, chilo: 1000, kilogramme: 1000, kilogrammes: 1000,
  ml: 1, milliliter: 1, millilitri: 1, millilitre: 1, // treat ml as g for beverages
  l: 1000, litro: 1000, litri: 1000, liter: 1000, litre: 1000, litres: 1000,
};

// Filler words to strip from the query
const FILLERS = new Set([
  "di", "del", "della", "dello", "dei", "delle", "degli", "il", "la", "lo",
  "un", "uno", "una",
  "of", "the", "a", "an",
  "von", "der", "die", "das",
  "de", "du", "la", "le", "les",
]);

function parseNumber(tok) {
  // Numeric?
  const clean = tok.replace(",", ".");
  const n = parseFloat(clean);
  if (!Number.isNaN(n) && /^\d/.test(clean)) return n;
  const w = WORD_NUMS[tok.toLowerCase()];
  return typeof w === "number" ? w : null;
}

function cleanQuery(words) {
  return words
    .map((w) => w.toLowerCase())
    .filter((w) => !FILLERS.has(w))
    .join(" ")
    .replace(/[.,!?;:]+$/g, "")
    .trim();
}

export function parseVoiceFood(rawText) {
  const text = (rawText || "").trim().replace(/\s+/g, " ");
  if (!text) return { query: "" };

  const tokens = text.split(" ");
  let quantityG = null;
  let pieces = null;
  const remaining = [];

  // Pattern A: "<number> <unit>" anywhere in the sentence.
  //   e.g. "kiwi 100 g", "100 grammi pollo"
  for (let i = 0; i < tokens.length; i++) {
    const numTok = tokens[i].replace(/[^\d.,a-zA-ZÀ-ÿ]/g, "");
    const num = parseNumber(numTok);
    if (num == null) { remaining.push(tokens[i]); continue; }
    // Look ahead for a unit
    const next = (tokens[i + 1] || "").toLowerCase().replace(/[^a-zà-ÿ]/g, "");
    if (UNITS[next]) {
      quantityG = num * UNITS[next];
      i += 1;
      continue;
    }
    // Compact form "100g", "100gr", "100grammi"
    const compact = numTok.match(/^([\d.,]+)([a-zà-ÿ]+)$/i);
    if (compact) {
      const cnum = parseFloat(compact[1].replace(",", "."));
      const u = compact[2].toLowerCase();
      if (UNITS[u]) { quantityG = cnum * UNITS[u]; continue; }
    }
    // Bare number → pieces
    if (pieces == null) pieces = num;
    else remaining.push(tokens[i]);
  }

  let query = cleanQuery(remaining);
  // Trim leading/trailing "di"/"of"/... one more time to be safe.
  query = query.replace(/^(di|of|de|von)\s+/i, "").trim();

  const out = { query };
  if (quantityG != null && quantityG > 0 && quantityG < 5000) out.quantity_g = Math.round(quantityG);
  if (pieces != null && pieces > 0 && pieces <= 20) out.pieces = pieces;
  return out;
}

// Split a voice transcription that contains multiple foods separated by
// "e"/"and"/"und"/"et"/","/"+" into individual parsed items. Returns
// { items: [{query, quantity_g?, pieces?}, ...] } — always at least length 1
// (may be empty if no valid food name was detected).
//
// Examples:
//   "yogurt greco e due mele" → [{query:"yogurt greco"}, {query:"mele", pieces:2}]
//   "100g pasta e 50g pomodoro" → [{query:"pasta", quantity_g:100}, {query:"pomodoro", quantity_g:50}]
export function parseVoiceFoodList(rawText) {
  const text = (rawText || "").trim().replace(/\s+/g, " ");
  if (!text) return { items: [] };

  // Split on separator words / punctuation. We use word-boundaries so we
  // don't split things like "burr" inside "burrata". Also handle ";" and "+".
  const parts = text
    .split(/\s*(?:,|;|\+|\s\/\s|\s(?:e|ed|and|und|et|plus)\s)\s*/i)
    .map((s) => s.trim())
    .filter(Boolean);

  const items = parts
    .map((p) => parseVoiceFood(p))
    .filter((it) => it.query && it.query.length >= 2);

  return { items };
}
